<template>
  <div class="flex h-full w-full flex-col overflow-hidden bg-gray-50 text-gray-900">

    <!-- Studio Header -->
    <header class="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4 shrink-0 z-50">
      <div class="flex items-center gap-3">
        <el-button circle text @click="toggleDataPanel" :type="(isMobile && showMobileDrawer && mobileActiveTab === 'data') || (!isMobile && showDataPanel) ? 'primary' : ''" :plain="(isMobile && showMobileDrawer && mobileActiveTab === 'data') || (!isMobile && showDataPanel)">
          <el-icon :size="18"><Menu /></el-icon>
        </el-button>

        <div class="flex items-center gap-3 ml-2 border-l border-gray-200 pl-4 h-6">
           <div class="flex items-center justify-center w-8 h-8 rounded bg-gradient-to-br from-blue-600 to-indigo-600 text-white font-bold">S</div>
           <span class="font-bold text-lg tracking-tight hidden md:inline">Sankey<span class="text-blue-600">Master</span></span>
        </div>
      </div>

      <div class="flex items-center gap-2">
         <el-button circle text @click="toggleOptionsPanel" :type="(isMobile && showMobileDrawer && mobileActiveTab === 'options') || (!isMobile && showOptionsPanel) ? 'primary' : ''" :plain="(isMobile && showMobileDrawer && mobileActiveTab === 'options') || (!isMobile && showOptionsPanel)">
           <el-icon :size="18"><Setting /></el-icon>
         </el-button>
      </div>
    </header>

    <!-- Main Workspace -->
    <div class="flex-1 flex overflow-hidden relative">

      <!-- Left Panel (Desktop Only) -->
      <transition name="slide-left">
        <aside
          v-if="!isMobile"
          v-show="showDataPanel"
          class="bg-white border-r border-gray-200 flex flex-col z-40 w-80 relative transition-all duration-300"
        >
           <LeftSidebar
            :chart-data="chartData"
            :source="source"
            :target="target"
            :value="value"
            @open-price="priceVisible = true"
            @add-data="addData"
            @delete-data="deleteData"
            @change-lang="changeLang"
            @open-service="serviceVisible = true"
           />
        </aside>
      </transition>

      <!-- Center Canvas -->
      <main class="flex-1 bg-gray-100 bg-dot-pattern relative overflow-hidden flex flex-col items-center justify-center">

        <!-- Zoom/Toolbar Floating Pill -->
        <div class="absolute top-4 z-20 bg-white/90 backdrop-blur rounded-full shadow-sm border border-gray-200 px-3 py-1.5 flex items-center gap-2">
           <el-button circle text size="small" @click="zoomOut"><el-icon><Remove /></el-icon></el-button>
           <span class="text-xs font-mono w-12 text-center text-gray-500">{{ Math.round(zoomLevel * 100) }}%</span>
           <el-button circle text size="small" @click="zoomIn"><el-icon><Plus /></el-icon></el-button>
           <div class="w-px h-3 bg-gray-300 mx-1"></div>
           <el-button circle text size="small" @click="resetZoom"><el-icon><Refresh /></el-icon></el-button>
        </div>

        <!-- The Artboard -->
        <div
          class="relative transition-transform duration-200 ease-out"
          :style="artboardStyle"
        >
           <SankeyChart
             ref="chartAreaRef"
             :image-width="imageWidth"
             :image-height="imageHeight"
             :background-color="backgroundColor"
             :zoom-level="zoomLevel"
             :chart-data="chartData"
             :orient="orient"
             :node-align="nodeAlign"
             :label-position="labelPosition"
             :line-style-color="lineStyleColor"
             :selected-line-color-curveness="selectedLineColorCurveness"
             :node-width="nodeWidth"
             :node-gap="nodeGap"
             :line-opacity="lineOpacity"
             :label-font-size="labelFontSize"
           />
        </div>

      </main>

      <!-- Right Panel (Desktop Only) -->
      <transition name="slide-right">
        <aside
          v-if="!isMobile"
          v-show="showOptionsPanel"
          class="bg-white border-l border-gray-200 flex flex-col z-40 w-80 relative transition-all duration-300"
        >
          <RightSidebar
            v-model:image-width="imageWidth"
            v-model:image-height="imageHeight"
            v-model:background-color="backgroundColor"
            v-model:orient="orient"
            v-model:node-align="nodeAlign"
            v-model:label-position="labelPosition"
            v-model:line-style-color="lineStyleColor"
            v-model:selected-line-color-curveness="selectedLineColorCurveness"
            v-model:node-width="nodeWidth"
            v-model:node-gap="nodeGap"
            v-model:line-opacity="lineOpacity"
            v-model:label-font-size="labelFontSize"
            :predefine-colors="predefineColors"
            @export-layout="exportLayout"
            @export-s-v-g="exportSVG"
            @update-chart="updateChart"
          />
        </aside>
      </transition>

      <!-- Unified Mobile Drawer -->
      <transition name="slide-up">
        <div v-if="isMobile && showMobileDrawer" class="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-xl shadow-2xl flex flex-col h-[70vh] border-t border-gray-200">

           <!-- Mobile Drawer Header -->
           <div class="h-12 border-b border-gray-100 flex items-center px-4 bg-gray-50 rounded-t-xl shrink-0">
              <div class="flex-1 flex justify-center gap-1 bg-gray-200/50 p-1 rounded-lg">
                  <button
                    class="flex-1 py-1 px-3 text-xs font-medium rounded-md transition-all"
                    :class="mobileActiveTab === 'data' ? 'bg-white shadow text-blue-600' : 'text-gray-500'"
                    @click="mobileActiveTab = 'data'"
                  >
                    {{ $t('Data') }}
                  </button>
                  <button
                    class="flex-1 py-1 px-3 text-xs font-medium rounded-md transition-all"
                    :class="mobileActiveTab === 'options' ? 'bg-white shadow text-blue-600' : 'text-gray-500'"
                    @click="mobileActiveTab = 'options'"
                  >
                    {{ $t('Options') }}
                  </button>
              </div>
              <el-button circle text class="ml-3" @click="showMobileDrawer = false">
                 <el-icon><Close /></el-icon>
              </el-button>
           </div>

           <!-- Content Area -->
           <div class="flex-1 overflow-hidden relative">
              <div v-if="mobileActiveTab === 'data'" class="h-full">
                  <LeftSidebar
                    :chart-data="chartData"
                    :source="source"
                    :target="target"
                    :value="value"
                    @open-price="priceVisible = true"
                    @add-data="addData"
                    @delete-data="deleteData"
                    @change-lang="changeLang"
                    @open-service="serviceVisible = true"
                  />
              </div>
              <div v-if="mobileActiveTab === 'options'" class="h-full">
                  <RightSidebar
                    v-model:image-width="imageWidth"
                    v-model:image-height="imageHeight"
                    v-model:background-color="backgroundColor"
                    v-model:orient="orient"
                    v-model:node-align="nodeAlign"
                    v-model:label-position="labelPosition"
                    v-model:line-style-color="lineStyleColor"
                    v-model:selected-line-color-curveness="selectedLineColorCurveness"
                    :predefine-colors="predefineColors"
                    @export-layout="exportLayout"
                    @update-chart="updateChart"
                  />
              </div>
           </div>
        </div>
      </transition>

      <!-- Mobile Overlay Backdrop -->
      <div v-if="isMobile && showMobileDrawer" class="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm" @click="showMobileDrawer = false"></div>

    </div>

    <!-- 价格/账户对话框 -->
    <el-dialog
      v-model="priceVisible"
      :show-close="false"
      :width="isMobile ? '95%' : '750px'"
      append-to-body
      class="clean-dialog"
    >
      <div class="relative">
        <el-button
          class="absolute -top-2 right-0 z-20"
          text
          circle
          @click="priceVisible = false"
        >
          <el-icon :size="20"><Close /></el-icon>
        </el-button>
        <el-tabs>
          <el-tab-pane :label="$t('Plans')">
            <PricePage :in-dialog="true" />
          </el-tab-pane>
          <el-tab-pane :label="$t('Account')">
            <Account :in-dialog="true" />
          </el-tab-pane>
        </el-tabs>
      </div>
    </el-dialog>

    <el-dialog
      v-model="serviceVisible"
      :title="$t('Service')"
      :width="isMobile ? '95%' : '600px'"
      append-to-body
    >
      <Service />
    </el-dialog>

  </div>
</template>

<script setup>
import { ElNotification } from 'element-plus'
import { Menu, Setting, Remove, Plus, Refresh, Close } from '@element-plus/icons-vue'
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import PricePage from './PricePage.vue'
import Service from './Service.vue'
import Account from './Account.vue'
import LeftSidebar from './home/LeftSidebar.vue'
import RightSidebar from './home/RightSidebar.vue'
import SankeyChart from './home/SankeyChart.vue'

// 使用i18n
const { t, locale } = useI18n({
  useScope: 'global'
})

// 响应式状态
const showDataPanel = ref(true)
const showOptionsPanel = ref(true)

// Mobile
const isMobile = ref(false)
const showMobileDrawer = ref(false)
const mobileActiveTab = ref('data')

// 数据状态
const chartData = ref([])
const source = ref('')
const target = ref('')
const value = ref(0)

// 图表配置
const backgroundColor = ref('#FFFFFF')
const imageHeight = ref(512)
const imageWidth = ref(512)
const orient = ref('horizontal')
const nodeAlign = ref('right')
const labelPosition = ref('top')
const lineStyleColor = ref('source')
const selectedLineColorCurveness = ref(0.1)
const nodeWidth = ref(18)
const nodeGap = ref(14)
const lineOpacity = ref(0.42)
const labelFontSize = ref(12)
const zoomLevel = ref(1)

// 界面状态
const priceVisible = ref(false)
const serviceVisible = ref(false)
const selectedLang = ref(null)

// 引用
const chartAreaRef = ref(null)

// 预定义颜色
const predefineColors = ref([
  '#ff4500', '#ff8c00', '#ffd700', '#90ee90', '#00ced1', '#1e90ff', '#c71585',
  'rgba(255, 69, 0, 0.68)', 'rgb(255, 120, 0)', 'hsv(51, 100, 98)',
  'hsva(120, 40, 94, 0.5)', 'hsl(181, 100%, 37%)', 'hsla(209, 100%, 56%, 0.73)', '#c7158577'
])

// Computed
const artboardStyle = computed(() => ({
  transform: `scale(${zoomLevel.value})`,
  transformOrigin: 'center center'
}))

// 方法
const checkMobile = () => {
  isMobile.value = window.innerWidth <= 1024
  if (isMobile.value) {
    showDataPanel.value = false
    showOptionsPanel.value = false
  }
}

const toggleDataPanel = () => {
  if (isMobile.value) {
    mobileActiveTab.value = 'data'
    showMobileDrawer.value = true
  } else {
    showDataPanel.value = !showDataPanel.value
  }
}

const toggleOptionsPanel = () => {
  if (isMobile.value) {
    mobileActiveTab.value = 'options'
    showMobileDrawer.value = true
  } else {
    showOptionsPanel.value = !showOptionsPanel.value
  }
}

const zoomIn = () => {
  zoomLevel.value = Math.min(zoomLevel.value * 1.2, 3)
}

const zoomOut = () => {
  zoomLevel.value = Math.max(zoomLevel.value / 1.2, 0.1)
}

const resetZoom = () => {
  zoomLevel.value = 1
}

const changeLang = (lang) => {
  locale.value = lang
}

const Notify = () => {
  ElNotification({
    title: t("Warning"),
    message: t("InvalidInput"),
    position: 'top-left',
    type: 'warning'
  })
}

const hasPurchased = () => {
  const purchaseStatus = localStorage.getItem("purchaseStatus")
  const expirationTime = localStorage.getItem("expirationTime")
  return purchaseStatus === "purchased" && expirationTime && new Date() < new Date(expirationTime)
}

const addData = (data) => {
  if (hasPurchased()) {
    if (data.source && data.target && data.value > 0) {
      chartData.value.push({ source: data.source, target: data.target, value: data.value })
      updateChart()
    } else {
      Notify()
    }
  } else {
    priceVisible.value = true
  }
}

const deleteData = (index) => {
  chartData.value.splice(index, 1)
  updateChart()
}

const updateChart = () => {
  nextTick(() => {
    if (chartAreaRef.value?.updateChart) {
      chartAreaRef.value.updateChart()
    }
  })
}

const exportLayout = () => {
  if (!chartAreaRef.value) {
    ElNotification({ title: t('Error'), message: t('ChartNotReady'), type: 'error' })
    return
  }
  try { chartAreaRef.value.exportLayout() } catch (error) {
    console.error('导出失败:', error)
    ElNotification({ title: t('Error'), message: t('ExportFailed'), type: 'error' })
  }
}

const exportSVG = () => {
  if (!chartAreaRef.value) {
    ElNotification({ title: t('Error'), message: t('ChartNotReady'), type: 'error' })
    return
  }
  try { chartAreaRef.value.exportSVG() } catch (error) {
    console.error('SVG导出失败:', error)
    ElNotification({ title: t('Error'), message: t('ExportFailed'), type: 'error' })
  }
}

const generateData = () => {
  chartData.value = [
    { source: 'A', target: 'B', value: 5 },
    { source: 'B', target: 'C', value: 3 },
    { source: 'B', target: 'D', value: 2 },
    { source: 'E', target: 'A', value: 4 },
    { source: 'M', target: 'B', value: 4 },
    { source: 'X', target: 'M', value: 4 },
    { source: 'Z', target: 'A', value: 4 },
    { source: '0', target: 'E', value: 4 }
  ]
}

// 生命周期
onMounted(() => {
  checkMobile()
  selectedLang.value = locale.value
  generateData()
  window.addEventListener('resize', checkMobile)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
})

// 监听器
watch(zoomLevel, () => {
  // zoom变化时图表会自动响应
})
</script>

<style scoped>
/* Transitions for Panels */
.slide-left-enter-active, .slide-left-leave-active,
.slide-right-enter-active, .slide-right-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-left-enter-from, .slide-left-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.slide-right-enter-from, .slide-right-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
</style>
<style>
.slide-up-enter-active, .slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.slide-up-enter-from, .slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
